import type { ComponentType, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ArrowLeft,
  Building,
  CalendarClock,
  Globe2,
  Link2,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { ProviderLayout } from '@/components/layouts/provider-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useProviderCustomer } from '@/hooks/provider/useProviderCustomers';
import { useIssueCustomerPortalLink } from '@/hooks/provider/useProviderApiTokens';
import { useProviderCheckoutFieldsSettings } from '@/hooks/provider/useProviderCheckoutFieldsSettings';
import { toast } from 'sonner';
import { analyticsService } from '@/services/analytics.service';
import type { ProviderCheckoutFieldKey } from '@/types/provider-checkout-fields';
import type { ProviderCustomerDetail } from '@/types/provider-customers';

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const statusVariants: Record<string, { labelKey: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  active: { labelKey: 'customers.providerList.statusFallback.active', variant: 'default' },
  inactive: { labelKey: 'customers.providerList.statusFallback.inactive', variant: 'secondary' },
  suspended: { labelKey: 'customers.providerList.statusFallback.suspended', variant: 'destructive' },
  trial: { labelKey: 'customers.providerList.statusFallback.trial', variant: 'secondary' },
  pending_verification: {
    labelKey: 'customers.providerList.statusFallback.pendingVerification',
    variant: 'secondary',
  },
};

const CHECKOUT_FIELD_ICON_MAP: Record<
  ProviderCheckoutFieldKey,
  ComponentType<{ className?: string }>
> = {
  company: Building,
  contact_phone: Phone,
  'billing.address_line_1': MapPin,
  'billing.address_line_2': MapPin,
  'billing.address_city': MapPin,
  'billing.address_state': MapPin,
  'billing.address_postal_code': MapPin,
  'billing.address_country': Globe2,
};

const CHECKOUT_FIELD_LABEL_KEY_MAP: Record<ProviderCheckoutFieldKey, string> = {
  company: 'settings.checkoutFields.fields.company',
  contact_phone: 'settings.checkoutFields.fields.contact_phone',
  'billing.address_line_1': 'settings.checkoutFields.fields.billing.address_line_1',
  'billing.address_line_2': 'settings.checkoutFields.fields.billing.address_line_2',
  'billing.address_city': 'settings.checkoutFields.fields.billing.address_city',
  'billing.address_state': 'settings.checkoutFields.fields.billing.address_state',
  'billing.address_postal_code': 'settings.checkoutFields.fields.billing.address_postal_code',
  'billing.address_country': 'settings.checkoutFields.fields.billing.address_country',
};

const formatCountryValue = (value: string | null | undefined, language: string): string => {
  const code = value?.trim().toUpperCase();
  if (!code) {
    return '—';
  }

  if (!/^[A-Z]{2}$/.test(code)) {
    return code;
  }

  try {
    const displayName = new Intl.DisplayNames([language || 'en'], { type: 'region' }).of(code);
    if (displayName && displayName !== code) {
      return `${displayName} (${code})`;
    }
  } catch {
    // Ignore locale edge cases and fall back to raw code.
  }

  return code;
};

const resolveCheckoutFieldValue = (
  customer: ProviderCustomerDetail,
  key: ProviderCheckoutFieldKey,
  language: string,
): string => {
  const profile = customer.checkoutProfile;

  switch (key) {
    case 'company':
      return profile?.company ?? customer.companyName ?? '—';
    case 'contact_phone':
      return profile?.contactPhone ?? customer.contactPhone ?? '—';
    case 'billing.address_line_1':
      return profile?.billingAddressLine1 ?? '—';
    case 'billing.address_line_2':
      return profile?.billingAddressLine2 ?? '—';
    case 'billing.address_city':
      return profile?.billingAddressCity ?? '—';
    case 'billing.address_state':
      return profile?.billingAddressState ?? '—';
    case 'billing.address_postal_code':
      return profile?.billingAddressPostalCode ?? '—';
    case 'billing.address_country':
      return formatCountryValue(profile?.billingAddressCountry, language);
    default:
      return '—';
  }
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, error, refetch } = useProviderCustomer(id);
  const checkoutFieldsQuery = useProviderCheckoutFieldsSettings(true, i18n.language);
  const issueLink = useIssueCustomerPortalLink();

  const customer = data;

  const statusConfig = useMemo(() => {
    if (!customer) {
      return statusVariants.inactive;
    }
    return statusVariants[customer.status] ?? statusVariants.inactive;
  }, [customer]);

  const primaryTeamRole = customer?.teamRoles.find((role) => role.role !== 'individual');
  const hasTeamMembership = Boolean(primaryTeamRole);
  const enabledCheckoutFields = useMemo(
    () =>
      (checkoutFieldsQuery.data?.fields ?? [])
        .filter((field) => field.enabled)
        .slice()
        .sort((a, b) => {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
          return a.key.localeCompare(b.key);
        }),
    [checkoutFieldsQuery.data?.fields],
  );
  const checkoutFieldTiles = useMemo(() => {
    if (!customer) {
      return [];
    }

    const tiles = enabledCheckoutFields.map((field) => ({
      key: field.key,
      icon: CHECKOUT_FIELD_ICON_MAP[field.key],
      title: t(CHECKOUT_FIELD_LABEL_KEY_MAP[field.key]),
      value: resolveCheckoutFieldValue(customer, field.key, i18n.language),
    }));

    // Keep line 1 before line 2 for readability in the detail view.
    const line1Index = tiles.findIndex((tile) => tile.key === 'billing.address_line_1');
    const line2Index = tiles.findIndex((tile) => tile.key === 'billing.address_line_2');
    if (line1Index !== -1 && line2Index !== -1 && line1Index > line2Index) {
      const next = [...tiles];
      const [line1Tile] = next.splice(line1Index, 1);
      next.splice(line2Index, 0, line1Tile);
      return next;
    }

    return tiles;
  }, [customer, enabledCheckoutFields, i18n.language, t]);

  const handleIssuePortalLink = async () => {
    if (!id) return;

    try {
      const response = await issueLink.mutateAsync({ customerId: id });
      analyticsService.track('provider_customer_portal_link_opened', {
        customerId: id,
        url: response.url,
        expiresAt: response.expiresAt,
      });
      toast.success(t('customers.detail.ssoLinkIssued', 'Customer portal opened in a new tab.'));
      window.open(response.url, '_blank', 'noopener');
    } catch (mutationError) {
      const message =
        mutationError instanceof Error
          ? mutationError.message
          : t('customers.detail.ssoLinkError', 'Unable to issue portal link. Try again later.');
      toast.error(message);
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <ProviderLayout>
      <div className="container mx-auto space-y-6 px-6 py-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/provider/customers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              {customer?.name ?? t('customers.detail.loadingName', 'Customer')}
            </h1>
            {customer?.companyName ? (
              <p className="text-muted-foreground">{customer.companyName}</p>
            ) : null}
          </div>
          {customer && (
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="default"
                  className="gap-2"
                  disabled={issueLink.isPending || isLoading}
                >
                  <Link2 className="h-4 w-4" />
                  {t('customers.detail.openPortal', 'Open customer portal')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('customers.detail.ssoConfirmTitle', 'Generate secure portal link?')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t(
                      'customers.detail.ssoConfirmDescription',
                      'A single-use magic link will be issued for {{name}}. They will be signed into the portal immediately.',
                      { name: customer.name },
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleIssuePortalLink} disabled={issueLink.isPending}>
                    {issueLink.isPending
                      ? t('customers.detail.ssoIssuing', 'Generating link…')
                      : t('customers.detail.ssoConfirmAction', 'Issue link')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>
                {error instanceof Error
                  ? error.message
                  : t('customers.detail.loadError', 'Failed to load customer.')}
              </span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                {t('customers.detail.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              {t('customers.detail.profileTitle', 'Profile overview')}
            </CardTitle>
            <CardDescription>
              {t('customers.detail.profileSubtitle', 'Core attributes synced from your automation platform.')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} className="h-24 w-full" />
                ))}
              </div>
            ) : customer ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <SummaryTile
                  icon={Mail}
                  title={t('customers.detail.email', 'Email')}
                  value={customer.email}
                />
                <SummaryTile
                  icon={CalendarClock}
                  title={t('customers.detail.createdAt', 'Joined Florentin')}
                  value={formatDate(customer.createdAt)}
                />
                <SummaryTile
                  icon={ShieldCheck}
                  title={t('customers.detail.status', 'Status')}
                  value={
                    <Badge variant={statusConfig.variant}>
                      {t(statusConfig.labelKey, { defaultValue: customer.status })}
                    </Badge>
                  }
                />
                <SummaryTile
                  icon={Users}
                  title={t('customers.detail.teamRoles', 'Teams & roles')}
                  value={
                    customer.teamRoles.length > 0 ? (
                      <div className="space-y-1">
                        {customer.teamRoles.map((role) => (
                          <div key={`${role.teamId ?? 'solo'}:${role.role}`} className="text-sm">
                            {role.display}
                          </div>
                        ))}
                      </div>
                    ) : (
                      t('customers.detail.noTeams', 'Individual account')
                    )
                  }
                />
                <SummaryTile
                  icon={AlertCircle}
                  title={t('customers.detail.lastActivity', 'Last login')}
                  value={formatDate(customer.lastLoginAt)}
                />
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {t('customers.detail.noData', 'Customer details unavailable.')}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {t('customers.detail.checkoutProfileTitle', 'Checkout profile')}
            </CardTitle>
            <CardDescription>
              {t(
                'customers.detail.checkoutProfileSubtitle',
                'Latest checkout data collected for this customer.',
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading || checkoutFieldsQuery.isLoading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-24 w-full" />
                ))}
              </div>
            ) : checkoutFieldTiles.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {checkoutFieldTiles.map((tile) => (
                  <SummaryTile
                    key={tile.key}
                    icon={tile.icon}
                    title={tile.title}
                    value={tile.value}
                  />
                ))}
              </div>
            ) : (
              <div className="py-2 text-sm text-muted-foreground">
                {t(
                  'customers.detail.checkoutProfileEmpty',
                  'No checkout fields are enabled for this provider.',
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('customers.detail.nextStepsTitle', 'Automation tips')}</CardTitle>
            <CardDescription>
              {t(
                'customers.detail.nextStepsDescription',
                'Use service tokens to schedule syncs and deliver a seamless customer portal experience.',
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div>
              <strong className="text-foreground">
                {t('customers.detail.tipTokens', 'Rotate portal tokens regularly')}
              </strong>
              <p>
                {t(
                  'customers.detail.tipTokensDescription',
                  'Remove unused service tokens in the Provider API area to prevent stale automation credentials.',
                )}
              </p>
            </div>
            <div>
              <strong className="text-foreground">
                {t('customers.detail.tipSso', 'Audit SSO activity')}
              </strong>
              <p>
                {t(
                  'customers.detail.tipSsoDescription',
                  'Track issued links and disable any integration issuing unexpected portal sessions.',
                )}
              </p>
            </div>
            {hasTeamMembership ? (
              <div>
                <strong className="text-foreground">
                  {t('customers.detail.tipTeams', 'Team access detected')}
                </strong>
                <p>
                  {t(
                    'customers.detail.tipTeamsDescription',
                    'Team owners will inherit the same portal access granted through service tokens. Confirm the destination app enforces your seat limits.',
                  )}
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </ProviderLayout>
  );
}

interface SummaryTileProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  value: ReactNode;
}

function SummaryTile({ icon: Icon, title, value }: SummaryTileProps) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon className="h-4 w-4" />
        {title}
      </div>
      <div className="mt-2 text-sm text-foreground">{value}</div>
    </div>
  );
}
